import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Layout,
  Typography,
  Form,
  Input,
  Button,
  Space,
  Avatar,
  notification,
  Row,
  Col,
  Modal,
  Tooltip,
} from 'antd';
import { UserOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './Login.css';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch('/backend/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: values.identifier,
          password: values.password,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        notification.success({
          message: 'Login Successful',
          description: `Welcome!`,
          duration: 3,
        });
        navigate('/home');
        form.resetFields();
      } else {
        let errorMessage = 'An error occurred during login.';
        if (data.error === 'User not found') {
          errorMessage = 'No account found with this email or username.';
        } else if (data.error === 'Invalid password') {
          errorMessage = 'Incorrect password.';
        } else if (data.error) {
          errorMessage = data.error;
        }
        setModalMessage(errorMessage);
        setIsModalVisible(true);
      }
    } catch (error) {
      setModalMessage('Network error. Please check your connection.');
      setIsModalVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    notification.error({
      message: 'Validation Failed',
      description: 'Please check your credentials.',
      duration: 3,
    });
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setModalMessage('');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          background: 'rgba(255,255,255,0.95)',
          padding: '0 4vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 15px rgba(0, 0, 0, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: '1px solid #f0f0f0',
          backdropFilter: 'blur(10px)',
          borderRadius: '0 0 18px 18px',
          minHeight: 56,
          width: '100vw',
          maxWidth: '100vw',
          overflowX: 'auto',
        }}
        aria-label="NH Guide Header"
      >
        <Space align="center" style={{ minWidth: 0 }}>
          <Avatar
            src="components/nhguidelogo.jpg"
            alt="NH Guide Logo"
            style={{ marginRight: 8, backgroundColor: '#4a90e2', width: 32, height: 32, minWidth: 32, minHeight: 32 }}
            size={32}
          />
          <Title level={4} style={{
            margin: 0,
            color: '#1677ff',
            fontWeight: 700,
            fontSize: 18,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 100,
            display: 'block',
            letterSpacing: 0.5,
            background: 'linear-gradient(135deg, #1677ff 0%, #722ed1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            NH Guide
          </Title>
        </Space>
        <div style={{ flex: 1 }} />
        <Space size={8} style={{ minWidth: 0 }}>
          <Button
            type="link"
            href="/register"
            style={{ color: '#333', fontWeight: 600, padding: '0 8px', fontSize: 13, borderRadius: 8 }}
            onMouseEnter={(e) => (e.target.style.color = '#1677ff')}
            onMouseLeave={(e) => (e.target.style.color = '#333')}
          >
            Register
          </Button>
          <Button
            type="link"
            href="/AdminLogin"
            style={{ color: '#333', fontWeight: 600, padding: '0 8px', fontSize: 13, borderRadius: 8 }}
            onMouseEnter={(e) => (e.target.style.color = '#1677ff')}
            onMouseLeave={(e) => (e.target.style.color = '#333')}
          >
            Login as Service Provider
          </Button>
        </Space>
      </Header>
      <Content style={{ flex: 1, padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className={`login-form-container ${isLoading ? 'loading' : ''}`}>
          <Title
            level={3}
            style={{ textAlign: 'center', color: '#2c3e50', fontWeight: 800, marginBottom: 15, fontSize: 28 }}
            aria-label="Login Title"
          >
            Sign In
          </Title>
          <Text
            style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: 25, display: 'block', fontSize: 16, fontStyle: 'italic' }}
            aria-label="Welcome Message"
          >
            Welcome Back
          </Text>
          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            initialValues={{ remember: true }}
            style={{ maxWidth: '500px', width: '100%' }}
            validateTrigger="onBlur"
          >
            <Form.Item
              label="Email or Username"
              name="identifier"
              rules={[{ required: true, message: 'Email or username required!' }]}
              hasFeedback
            >
              <Input
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="user@gmail.com or username"
                size="large"
                className="custom-input"
                aria-label="Identifier Field"
              />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Password required!' }]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="Enter password"
                size="large"
                className="custom-input"
                aria-label="Password Field"
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="custom-button"
                style={{ width: '100%', background: 'linear-gradient(90deg, #2c3e50, #34495e)', borderColor: '#2c3e50' }}
                loading={isLoading}
                aria-label="Login Button"
              >
                Sign In
              </Button>
            </Form.Item>
            <Row justify="space-between" align="middle" style={{ marginTop: 15 }}>
              <Tooltip title="Click to reset your password">
                <Text style={{ fontSize: 12, color: '#7f8c8d' }}>
                  <Link to="/forgot-password" style={{ color: '#4a90e2', fontWeight: 600 }}>
                    Forgot password?
                  </Link>
                </Text>
              </Tooltip>
              <Text style={{ fontSize: 12, color: '#7f8c8d' }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: '#4a90e2', fontWeight: 600 }}>
                  Register here
                </Link>
              </Text>
            </Row>
          </Form>
        </div>
      </Content>
      <Footer
        style={{
          textAlign: 'center',
          padding: '15px 0',
          background: '#ffffff',
          boxShadow: '0 -2px 15px rgba(0, 0, 0, 0.05)',
          color: '#7f8c8d',
          fontSize: 12,
        }}
        aria-label="Footer"
      >
        <Text style={{ display: 'block', marginTop: 5, fontSize: 10 }}>
          © 2025 NH Guide. All rights reserved.
        </Text>
      </Footer>
      <Modal
        title="Login Error"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={handleModalOk}
            style={{ background: '#4a90e2', border: 'none', borderRadius: '8px', padding: '0 20px' }}
            aria-label="Close Modal"
          >
            OK
          </Button>,
        ]}
        style={{ top: '20%', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)' }}
        bodyStyle={{ textAlign: 'center', fontSize: '16px', color: '#2c3e50', padding: '25px' }}
        aria-label="Error Modal"
      >
        <p>{modalMessage}</p>
      </Modal>
    </Layout>
  );
};

export default Login;
